import { useState, useRef, useEffect } from "react";
import { apiPost } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Music, Sparkles, Send, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const QUICK_PROMPTS = [
    "Book 2 hours for vocal recording tomorrow at 3pm",
    "I need mixing for a 4-track EP",
    "What’s available this Friday evening?",
    "Book a podcast recording next Tuesday at 10am",
];

export default function AIChatPage() {
    const [messages, setMessages] = useState([
        { role: "assistant", content: "Tell me the service and when you want it. I’ll handle the room and engineer automatically.", ts: new Date().toISOString() },
    ]);
    const [input, setInput] = useState("");
    const [convId, setConvId] = useState(null);
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages.length, sending]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg) return;
        setMessages((m) => [...m, { role: "user", content: msg, ts: new Date().toISOString() }]);
        setInput("");
        setSending(true);
        try {
            const res = await apiPost("/agent/chat", { message: msg, conversation_id: convId });
            setConvId(res.conversation_id);
            setMessages((m) => [...m, ...(res.messages || []).map((mm) => ({ ...mm, ts: mm.ts || new Date().toISOString() }))]);
            if (res.booking_id) toast.success("Booking created — confirmation sent.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Agent error");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="musook-page min-h-screen">
            <header className="border-b border-border bg-[hsl(var(--paper)/.9)] backdrop-blur">
                <div className="flex w-full items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/dashboard" className="flex items-center gap-3" data-testid="ai-brand">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background"><Music className="h-5 w-5" strokeWidth={1.75} /></div>
                        <div><div className="font-display text-lg font-semibold tracking-tight">Musook</div><div className="text-[10px] font-bold uppercase tracking-[.16em] text-muted-foreground">Booking desk</div></div>
                    </Link>
                    <Button asChild variant="ghost" size="sm"><Link to="/dashboard"><ArrowLeft className="mr-1 h-4 w-4" />Back to workspace</Link></Button>
                </div>
            </header>

            <main className="w-full px-4 py-6 sm:px-6 lg:py-8">

                <section className="min-w-0" data-testid="ai-booking-workspace">
                    <Card ref={scrollRef} className="musook-surface min-h-[52vh] max-h-[62vh] overflow-y-auto rounded-xl p-4 sm:p-6 pretty-scroll" data-testid="chat-window" aria-label="Booking agent conversation">
                        <div className="space-y-5">
                            {messages.map((m, i) => <div key={`msg-${i}-${m.ts || i}`} className={`flex gap-3 anim-fade-in-up ${m.role === "user" ? "justify-end" : ""}`}>
                                {m.role !== "user" && <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Sparkles className="h-4 w-4" /></div>}
                                <div className={`max-w-[88%] whitespace-pre-wrap rounded-xl px-4 py-3 text-sm leading-6 ${m.role === "user" ? "bg-primary text-primary-foreground" : "border border-border bg-secondary/60 text-foreground"}`} data-testid={`chat-message-${m.role}-${i}`}>
                                    {m.content}
                                    {m.booking_id && <div className="mt-2 text-[11px] opacity-70">Booking: {m.booking_id}</div>}
                                </div>
                            </div>)}
                            {sending && <div className="flex gap-3"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Sparkles className="h-4 w-4 anim-soft-pulse" /></div><div className="rounded-xl border border-border bg-secondary/60 px-4 py-3 text-sm text-muted-foreground">Checking the schedule…</div></div>}
                        </div>
                    </Card>

                    <div className="mt-4 flex flex-wrap gap-2" data-testid="chat-quick-prompts">
                        {QUICK_PROMPTS.map((p) => <button key={p} onClick={() => send(p)} disabled={sending} className="rounded-full border border-border bg-card px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary" data-testid={`chat-prompt-${p.slice(0, 12)}`}>{p}</button>)}
                    </div>
                    <div className="mt-4 flex items-end gap-2 rounded-xl border border-border bg-card p-2 shadow-sm">
                        <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Service + date/time…" className="min-h-[52px] max-h-[140px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0" data-testid="chat-input" />
                        <Button onClick={() => send()} disabled={sending || !input.trim()} className="mb-1" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
                    </div>
                    <p className="mt-2 text-center text-[11px] text-muted-foreground">Musook will confirm the room, engineer, timing, and booking details before it commits.</p>
                </section>
            </main>
        </div>
    );
}
